import { useState, useMemo } from "react";
import { ig } from "../inbox/_inbox_shared";
import { Search, X, Maximize2, ChevronDown } from "lucide-react";
import { TRIGGER_CATEGORIES, ACTION_CATEGORIES, NODE_STYLE, TONES } from "./_automation_data";

export function PickerPanel({ title, catalog, onClose, onPick, testId }) {
  const [tab, setTab] = useState("items");
  const [q, setQ] = useState("");
  const [collapsed, setCollapsed] = useState({});

  const visible = useMemo(() => {
    if (!q) return catalog;
    const ql = q.toLowerCase();
    return catalog.map((c) => ({
      ...c,
      items: c.items.filter((s) => s.toLowerCase().includes(ql)),
    })).filter((c) => c.items.length > 0);
  }, [q, catalog]);

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(7,31,24,0.32)", zIndex: 60 }}>
      <div onClick={(e) => e.stopPropagation()} data-testid={testId}
        style={{
          position: "absolute", right: 0, top: 0, bottom: 0, width: 460,
          background: ig.card, boxShadow: "-12px 0 32px rgba(7,31,24,0.18)",
          display: "flex", flexDirection: "column", fontFamily: "Inter, sans-serif",
        }}>
        <div style={{ padding: "16px 18px", borderBottom: `1px solid ${ig.border}`,
          display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontWeight: 800, fontSize: 15, flex: 1, color: ig.ink }}>{title}</span>
          <button data-testid="picker-expand" style={iconBtn}><Maximize2 size={13} /></button>
          <button data-testid="picker-close" onClick={onClose} style={iconBtn}><X size={13} /></button>
        </div>

        <div style={{ padding: "12px 18px 0", display: "flex", alignItems: "center", gap: 10 }}>
          <button style={iconBtnSquare}>⬚</button>
          <div style={{ flex: 1, position: "relative" }}>
            <Search size={13} style={{ position: "absolute", left: 11, top: "50%", transform: "translateY(-50%)", color: ig.muted }} />
            <input data-testid="picker-search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder={`Search ${title.toLowerCase().includes("trigger") ? "triggers" : "actions"}`}
              autoFocus
              style={{ width: "100%", height: 38, padding: "0 12px 0 34px", borderRadius: 9,
                border: `1.5px solid #2563EB`, background: ig.card, fontSize: 13, outline: "none", color: ig.ink }} />
          </div>
        </div>

        <div style={{ display: "flex", borderBottom: `1px solid ${ig.border}`, marginTop: 10, padding: "0 18px" }}>
          {[["items","Triggers"],["apps","Apps"]].map(([k, l]) => {
            const label = title.toLowerCase().includes("action") && k === "items" ? "Actions" : l;
            return (
              <button key={k} onClick={() => setTab(k)} style={{
                padding: "10px 16px", border: 0, background: "transparent", cursor: "pointer",
                color: tab === k ? "#2563EB" : ig.muted, fontWeight: 700, fontSize: 13,
                borderBottom: "2px solid " + (tab === k ? "#2563EB" : "transparent"),
              }}>{label === l && k === "items" && title.toLowerCase().includes("trigger") ? "Triggers" : (k === "items" && title.toLowerCase().includes("action") ? "Actions" : l)}</button>
            );
          })}
        </div>

        <div className="ig-scroll" style={{ flex: 1, overflowY: "auto", padding: "10px 14px 30px" }}>
          {tab === "apps" ? (
            <div style={{ padding: 30, textAlign: "center", color: ig.muted, fontSize: 13 }}>
              App marketplace integrations land in the next phase.
            </div>
          ) : visible.length === 0 ? (
            <div style={{ padding: 30, textAlign: "center", color: ig.muted, fontSize: 13 }}>No matches.</div>
          ) : visible.map((cat) => (
            <div key={cat.name} style={{
              border: `1px solid ${ig.border}`, borderRadius: 10, padding: "10px 12px",
              background: ig.card, marginBottom: 10,
            }}>
              <button onClick={() => setCollapsed((s) => ({ ...s, [cat.name]: !s[cat.name] }))} style={{
                width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
                border: 0, background: "transparent", cursor: "pointer", padding: 0, marginBottom: 4,
              }}>
                <span style={{ fontWeight: 800, fontSize: 13.5, color: ig.ink }}>{cat.name}</span>
                <ChevronDown size={14} color={ig.muted} style={{ transform: collapsed[cat.name] ? "rotate(-90deg)" : "rotate(0)", transition: "transform 160ms" }} />
              </button>
              {!collapsed[cat.name] && cat.items.map((s) => {
                const [key, rest] = s.split("::");
                const pro = (rest || "").endsWith("|pro");
                const label = (rest || "").replace(/\|pro$/, "");
                const ns = NODE_STYLE[key] || { tone: cat.name.toLowerCase().includes("event") || cat.name.toLowerCase().includes("communication") || cat.name === "Send data" ? "send" : (cat.name === "Internal" ? "logic" : "appt"), glyph: "•" };
                const tone = TONES[ns.tone] || TONES.logic;
                return (
                  <button key={`${cat.name}-${key}`} data-testid={`pick-${key}`} onClick={() => onPick(key, label)} style={{
                    display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "10px 8px",
                    border: 0, background: "transparent", borderRadius: 7, cursor: "pointer", textAlign: "left",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "#FAFCFB"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
                    <span style={{
                      width: 28, height: 28, borderRadius: 7, display: "grid", placeItems: "center",
                      background: tone.bg, color: tone.fg, fontSize: 13, fontWeight: 800,
                    }}>{ns.glyph}</span>
                    <span style={{ flex: 1, fontSize: 12.8, fontWeight: 600, color: ig.ink }}>{label}</span>
                    {pro && <span style={{ fontSize: 9.5, fontWeight: 800, color: "#7C3AED", padding: "2px 7px", borderRadius: 999, background: "#EDE9FE" }}>👑</span>}
                    <span style={{ color: ig.muted }}>›</span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const iconBtn = {
  width: 28, height: 28, borderRadius: 7, border: `1px solid ${ig.border}`, background: ig.card,
  display: "inline-grid", placeItems: "center", cursor: "pointer", color: ig.ink,
};
const iconBtnSquare = {
  width: 38, height: 38, borderRadius: 9, border: `1px solid ${ig.border}`, background: ig.card,
  display: "inline-grid", placeItems: "center", cursor: "pointer", color: ig.muted, fontSize: 16, padding: 0,
};

export default function _placeholder() { return null; }
